import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import ChangePasswordModal from "./components/ChangePasswordModal";

// LoginPage : import direct (1re page chargée, pas de gain à la lazy-loader)
import LoginPage from "./pages/LoginPage";

// Pages (lazy : chargées à la demande pour réduire le bundle initial)
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const UsersPage = lazy(() => import("./pages/UsersPage"));
const SitesPage = lazy(() => import("./pages/SitesPage"));
const SocietesLivraisonPage = lazy(() => import("./pages/SocietesLivraisonPage"));
const DossiersPage = lazy(() => import("./pages/DossiersPage"));
const DossierDetailPage = lazy(() => import("./pages/DossierDetailPage"));
const AnomaliesPage = lazy(() => import("./pages/AnomaliesPage"));
const AuditPage = lazy(() => import("./pages/AuditPage"));
const ReportingPage = lazy(() => import("./pages/ReportingPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const PermissionsPage = lazy(() => import("./pages/PermissionsPage"));
const GuidePage = lazy(() => import("./pages/GuidePage"));
const FournisseursPage = lazy(() => import("./pages/FournisseursPage"));
const FournisseurDetailPage = lazy(() => import("./pages/FournisseurDetailPage"));
const CommandesPage = lazy(() => import("./pages/CommandesPage"));
const StocksPage = lazy(() => import("./pages/StocksPage"));
const PlanningPage = lazy(() => import("./pages/PlanningPage"));

// Mobile Pages (lazy)
const MobileDashboard = lazy(() => import("./pages/mobile/MobileDashboard"));
const MobileDossiers = lazy(() => import("./pages/mobile/MobileDossiers"));
const MobileDossierDetail = lazy(() => import("./pages/mobile/MobileDossierDetail"));
const MobileVerification = lazy(() => import("./pages/mobile/MobileVerification"));
const MobileCouponRetrait = lazy(() => import("./pages/mobile/MobileCouponRetrait"));
const MobileHistorique = lazy(() => import("./pages/mobile/MobileHistorique"));
const MobileRemettre = lazy(() => import("./pages/mobile/MobileRemettre"));
const MobileReception = lazy(() => import("./pages/mobile/MobileReception"));
const MobileStock = lazy(() => import("./pages/mobile/MobileStock"));

// Layouts (import direct : utilisés sur quasi toutes les routes)
import BackofficeLayout from "./layouts/BackofficeLayout";
import MobileLayout from "./layouts/MobileLayout";

import "./App.css";

// Fallback affiché pendant le chargement d'un chunk lazy.
// Visuellement identique au spinner de ProtectedRoute pour ne pas troubler l'UX.
const LazyPageFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-700"></div>
  </div>
);

// Composant pour afficher le modal de changement de mot de passe
const PasswordChangeWrapper = ({ children }) => {
  const { user, mustChangePassword, clearMustChangePassword } = useAuth();
  
  return (
    <>
      {children}
      <ChangePasswordModal 
        open={mustChangePassword && !!user}
        onPasswordChanged={clearMustChangePassword}
        userName={user?.prenom || user?.login || ''}
      />
    </>
  );
};

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading, isAuthenticated } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-700"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect mobile users to mobile, admin to dashboard
    const mobileRoles = ['remettant_magasin', 'remettant_point_retrait'];
    if (mobileRoles.includes(user.role)) {
      return <Navigate to="/mobile" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

const AppRoutes = () => {
  const { user, isAuthenticated } = useAuth();
  
  // Determine home redirect based on user role
  const getHomeRedirect = () => {
    if (!isAuthenticated) return "/login";
    const mobileRoles = ['remettant_magasin', 'remettant_point_retrait'];
    if (mobileRoles.includes(user?.role)) return "/mobile";
    return "/dashboard";
  };

  const adminRoles = ['admin_kadolo', 'operator_kadolo', 'supervisor', 'auditeur'];
  const mobileRoles = ['remettant_magasin', 'remettant_point_retrait', 'admin_kadolo', 'operator_kadolo'];

  return (
    <Suspense fallback={<LazyPageFallback />}>
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      
      {/* Home redirect */}
      <Route path="/" element={<Navigate to={getHomeRedirect()} replace />} />
      
      {/* Back-office routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute allowedRoles={adminRoles}>
          <BackofficeLayout><DashboardPage /></BackofficeLayout>
        </ProtectedRoute>
      } />
      <Route path="/users" element={
        <ProtectedRoute allowedRoles={['admin_kadolo', 'operator_kadolo']}>
          <BackofficeLayout><UsersPage /></BackofficeLayout>
        </ProtectedRoute>
      } />
      <Route path="/sites" element={
        <ProtectedRoute allowedRoles={adminRoles}>
          <BackofficeLayout><SitesPage /></BackofficeLayout>
        </ProtectedRoute>
      } />
      <Route path="/societes-livraison" element={
        <ProtectedRoute allowedRoles={adminRoles}>
          <BackofficeLayout><SocietesLivraisonPage /></BackofficeLayout>
        </ProtectedRoute>
      } />
      <Route path="/dossiers" element={
        <ProtectedRoute allowedRoles={adminRoles}>
          <BackofficeLayout><DossiersPage /></BackofficeLayout>
        </ProtectedRoute>
      } />
      <Route path="/dossiers/:id" element={
        <ProtectedRoute allowedRoles={adminRoles}>
          <BackofficeLayout><DossierDetailPage /></BackofficeLayout>
        </ProtectedRoute>
      } />
      <Route path="/anomalies" element={
        <ProtectedRoute allowedRoles={adminRoles}>
          <BackofficeLayout><AnomaliesPage /></BackofficeLayout>
        </ProtectedRoute>
      } />
      <Route path="/audit" element={
        <ProtectedRoute allowedRoles={adminRoles}>
          <BackofficeLayout><AuditPage /></BackofficeLayout>
        </ProtectedRoute>
      } />
      <Route path="/reporting" element={
        <ProtectedRoute allowedRoles={adminRoles}>
          <BackofficeLayout><ReportingPage /></BackofficeLayout>
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute allowedRoles={['admin_kadolo']}>
          <BackofficeLayout><SettingsPage /></BackofficeLayout>
        </ProtectedRoute>
      } />
      <Route path="/permissions" element={
        <ProtectedRoute allowedRoles={['admin_kadolo']}>
          <BackofficeLayout><PermissionsPage /></BackofficeLayout>
        </ProtectedRoute>
      } />
      <Route path="/guide" element={
        <ProtectedRoute allowedRoles={adminRoles}>
          <BackofficeLayout><GuidePage /></BackofficeLayout>
        </ProtectedRoute>
      } />
      
      {/* Logistique routes */}
      <Route path="/fournisseurs" element={
        <ProtectedRoute allowedRoles={adminRoles}>
          <BackofficeLayout><FournisseursPage /></BackofficeLayout>
        </ProtectedRoute>
      } />
      <Route path="/fournisseurs/:id" element={
        <ProtectedRoute allowedRoles={adminRoles}>
          <BackofficeLayout><FournisseurDetailPage /></BackofficeLayout>
        </ProtectedRoute>
      } />
      <Route path="/commandes" element={
        <ProtectedRoute allowedRoles={adminRoles}>
          <BackofficeLayout><CommandesPage /></BackofficeLayout>
        </ProtectedRoute>
      } />
      <Route path="/stocks" element={
        <ProtectedRoute allowedRoles={adminRoles}>
          <BackofficeLayout><StocksPage /></BackofficeLayout>
        </ProtectedRoute>
      } />
      <Route path="/planning" element={
        <ProtectedRoute allowedRoles={adminRoles}>
          <BackofficeLayout><PlanningPage /></BackofficeLayout>
        </ProtectedRoute>
      } />
      
      {/* Mobile routes */}
      <Route path="/mobile" element={
        <ProtectedRoute allowedRoles={mobileRoles}>
          <MobileLayout><MobileDashboard /></MobileLayout>
        </ProtectedRoute>
      } />
      <Route path="/mobile/remettre" element={
        <ProtectedRoute allowedRoles={mobileRoles}>
          <MobileLayout><MobileRemettre /></MobileLayout>
        </ProtectedRoute>
      } />
      <Route path="/mobile/dossiers" element={
        <ProtectedRoute allowedRoles={mobileRoles}>
          <MobileLayout><MobileDossiers /></MobileLayout>
        </ProtectedRoute>
      } />
      <Route path="/mobile/dossiers/:id" element={
        <ProtectedRoute allowedRoles={mobileRoles}>
          <MobileLayout><MobileDossierDetail /></MobileLayout>
        </ProtectedRoute>
      } />
      <Route path="/mobile/dossiers/:id/verification" element={
        <ProtectedRoute allowedRoles={mobileRoles}>
          <MobileLayout><MobileVerification /></MobileLayout>
        </ProtectedRoute>
      } />
      <Route path="/mobile/dossiers/:id/coupon-fournisseur" element={
        <ProtectedRoute allowedRoles={mobileRoles}>
          <MobileLayout><MobileCouponRetrait /></MobileLayout>
        </ProtectedRoute>
      } />
      <Route path="/mobile/historique" element={
        <ProtectedRoute allowedRoles={mobileRoles}>
          <MobileLayout><MobileHistorique /></MobileLayout>
        </ProtectedRoute>
      } />
      <Route path="/mobile/reception" element={
        <ProtectedRoute allowedRoles={mobileRoles}>
          <MobileLayout><MobileReception /></MobileLayout>
        </ProtectedRoute>
      } />
      <Route path="/mobile/stock" element={
        <ProtectedRoute allowedRoles={mobileRoles}>
          <MobileLayout><MobileStock /></MobileLayout>
        </ProtectedRoute>
      } />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <PasswordChangeWrapper>
          <AppRoutes />
        </PasswordChangeWrapper>
        <Toaster 
          position="top-right" 
          richColors 
          closeButton
          toastOptions={{
            style: {
              fontFamily: 'Public Sans, sans-serif',
            },
          }}
        />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
